export type ConversationExecutionState =
  | "starting"
  | "queued"
  | "running"
  | "waiting_user_input"
  | "waiting_user_confirmation"
  | "waiting_operation"
  | "waiting_approval"
  | "cancelling"
  | "waiting_gate"
  | "stopping"
  | "dispatching"
  | "cancelled"
  | "failed"
  | "succeeded";

export type ConversationExecution = {
  state: ConversationExecutionState;
  run_id: string;
  node_id: string;
  run_version: number;
  interaction_version: number;
  missing_fields?: Array<{key: string; label: string}>;
  assistant_message?: string;
  interaction_turns?: Array<{
    interaction_version: number;
    source_message_id: string;
    assistant_message?: string;
    state: "collecting" | "waiting_input" | "waiting_confirmation" | "confirmed" | "cancelled";
    created_at: string;
  }>;
  manual_input_required?: boolean;
  replayed?: boolean;
};

export type ConversationThread = {
  id: string;
  title: string;
  state: "active" | "closed";
  focused_activity_id?: string;
  version: number;
  created_at: string;
  updated_at: string;
};

export type ConversationSummary = {
  id: string;
  title: string;
  last_message_preview: string;
  state: "active" | "closed";
  updated_at: string;
  read_only: boolean;
  read_only_reason: "" | "conversation_closed" | "task_in_progress_in_another_conversation";
};

export type ActiveTask = {
  thread_id: string;
  thread_title: string;
  display_name: string;
  state: string;
};

export type ConversationListResponse = {
  conversations: ConversationSummary[];
  active_task: ActiveTask | null;
  trace_id?: string;
};

export type ConversationOrchestrationView = {
  conversation: ConversationThread;
  messages?: Array<{id: string; sequence: number; role: "user" | "assistant"; kind: "text" | "clarification" | "status"; text: string; presentation?: unknown; created_at: string}>;
  activities?: Array<{id: string; state: string; execution_kind: string; execution_id?: string; source_message_id: string; version: number; created_at: string; updated_at: string}>;
  active_task?: ActiveTask | null;
  read_only?: boolean;
  read_only_reason?: "" | "conversation_closed" | "task_in_progress_in_another_conversation";
  focused_execution?: ConversationExecution;
  switch_decision?: {id: string; state: "pending" | "waiting_gate" | "stopping"; version: number; created_at: string};
  cancel_decision?: {id: string; activity_id: string; state: "pending" | "dispatching"; version: number; created_at: string};
  pending_workflow_proposal?: {id: string; display_name: string; state: "pending" | "accepted" | "rejected" | "superseded" | "expired"; version: number; expires_at: string; continuation?: boolean};
};

export type MessageReceipt = {
  conversation?: ConversationThread;
  message?: NonNullable<ConversationOrchestrationView["messages"]>[number];
  assistant_message?: NonNullable<ConversationOrchestrationView["messages"]>[number];
  activity?: NonNullable<ConversationOrchestrationView["activities"]>[number];
  execution?: ConversationExecution;
  pending_workflow_proposal?: ConversationOrchestrationView["pending_workflow_proposal"];
};

/** 写入已成功、详情同步失败；界面应恢复读取，绝不能提示重新发送。 */
export class ConversationSyncError extends Error {}

type FetchLike = (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;
type PendingMutation = {fingerprint: string; key: string};
type Listener = (view: ConversationOrchestrationView | null) => void;

/**
 * Conversation 请求错误保留 HTTP 状态和安全错误码。
 * 输入：API 响应与操作名称；输出：供 UI 区分登录失效、并发冲突和服务故障的错误对象。
 * 约束：只保存服务端允许暴露的 code，不保存响应正文中的内部异常信息。
 */
export class ConversationRequestError extends Error {
  constructor(message: string, readonly status: number, readonly code?: string) {
    super(message);
    this.name = "ConversationRequestError";
  }
}

async function requestError(response: Response, operation: string): Promise<ConversationRequestError> {
  let code: string | undefined;
  try {
    const payload = (await response.clone().json()) as {code?: unknown};
    if (typeof payload.code === "string") code = payload.code;
  } catch {
    // 错误响应可能没有 JSON；状态码仍足够完成安全分流。
  }
  return new ConversationRequestError(`${operation} failed: ${response.status}`, response.status, code);
}

export function isConversationUnauthenticatedError(error: unknown): boolean {
  return error instanceof ConversationRequestError && (error.status === 401 || error.status === 403);
}

const API_ROOT = "/api/user/conversation-orchestration";
export const CONVERSATION_CHANNEL = "softmap:user-conversation-orchestration:v1";
const UUID_V7 = /^[0-9a-f]{8}-[0-9a-f]{4}-7[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function requestKey(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return crypto.randomUUID();
  return `conversation-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}
function mutationFingerprint(operation: string, payload: unknown): string { return `${operation}\u0000${JSON.stringify(payload)}`; }
function validThreadID(threadID: string): boolean { return UUID_V7.test(threadID); }

/**
 * 浏览器侧 Conversation 客户端。所有已存在会话都必须通过显式 Thread ID 读取或发送；
 * epoch 令牌让切换会话时的迟到响应只能被丢弃，不能把旧会话写回当前 UI。
 */
export class ConversationOrchestrationClient {
  private view: ConversationOrchestrationView | null = null;
  private selectedThreadID: string | null = null;
  private epoch = 0;
  private listEpoch = 0;
  private pending: PendingMutation | null = null;
  private createPendingKey = "";
  private createInFlight: Promise<ConversationOrchestrationView | null> | null = null;
  private inFlight = new Map<string, Promise<ConversationOrchestrationView | null>>();
  private listeners = new Set<Listener>();
  private channel: BroadcastChannel | null = null;

  constructor(private readonly request: FetchLike = globalThis.fetch.bind(globalThis)) {
    if (typeof BroadcastChannel !== "undefined") {
      this.channel = new BroadcastChannel(CONVERSATION_CHANNEL);
      this.channel.onmessage = (event) => { if (event.data?.kind === "conversation-changed") void this.refresh(); };
    }
  }
  current(): ConversationOrchestrationView | null { return this.view; }
  selected(): string | null { return this.selectedThreadID; }
  subscribe(listener: Listener): () => void { this.listeners.add(listener); listener(this.view); return () => this.listeners.delete(listener); }
  close() { this.epoch++; this.listeners.clear(); this.channel?.close(); this.channel = null; }

  /** 旧创建合同保留给兼容调用方；新 UI 不调用它，因为空白会话不应提前落库。 */
  createConversation(): Promise<ConversationOrchestrationView | null> {
    if (this.createInFlight) return this.createInFlight;
    const key = this.createPendingKey || requestKey(); this.createPendingKey = key;
    const operation = (async () => {
      const response = await this.request(API_ROOT, {method: "POST", headers: {"content-type": "application/json", "idempotency-key": key}, body: "{}"});
      if (!response.ok) throw await requestError(response, "conversation create");
      this.createPendingKey = "";
      const payload = await response.json() as {conversation?: ConversationOrchestrationView};
      const view = payload.conversation ?? null;
      if (view) this.selectedThreadID = view.conversation.id;
      this.publish(view); return view;
    })();
    this.createInFlight = operation;
    void operation.then(() => { if (this.createInFlight === operation) this.createInFlight = null; }, () => { if (this.createInFlight === operation) this.createInFlight = null; });
    return operation;
  }

  async list(): Promise<ConversationListResponse> {
    const epoch = ++this.listEpoch;
    const response = await this.request(`${API_ROOT}/conversations`, {cache: "no-store"});
    if (!response.ok) throw await requestError(response, "conversation list");
    const payload = await response.json() as ConversationListResponse;
    // 列表只与列表竞态隔离；详情切换不能误伤已返回的历史摘要。
    if (epoch !== this.listEpoch) return {conversations: [], active_task: null};
    return {conversations: Array.isArray(payload.conversations) ? payload.conversations : [], active_task: payload.active_task ?? null, trace_id: payload.trace_id};
  }

  /** 选择会话只接受 UUIDv7，避免把任意路径片段传给 BFF。 */
  async select(threadID: string): Promise<ConversationOrchestrationView | null> {
    if (!validThreadID(threadID)) throw new Error("conversation thread id is invalid");
    this.selectedThreadID = threadID;
    return this.load(threadID);
  }

  hydrateReceipt(receipt: MessageReceipt): void {
    const thread = receipt.conversation;
    if (!thread || !validThreadID(thread.id)) return;
    this.selectedThreadID = thread.id;
    this.epoch++;
    this.publishReceipt(receipt);
  }

  /** 归档历史会话；服务端保留审计消息，前端从列表移除并清空当前选择。 */
  async archive(threadID: string): Promise<void> {
    if (!validThreadID(threadID)) throw new Error("conversation thread id is invalid");
    const response = await this.request(`${API_ROOT}/${threadID}`, {method: "DELETE", headers: {"idempotency-key": requestKey()}});
    if (!response.ok) throw await requestError(response, "conversation archive");
    if (this.selectedThreadID === threadID) this.clearSelection();
  }

  async load(threadID: string): Promise<ConversationOrchestrationView | null> {
    if (!validThreadID(threadID)) throw new Error("conversation thread id is invalid");
    const epoch = ++this.epoch;
    const response = await this.request(`${API_ROOT}/${threadID}`, {cache: "no-store"});
    if (!response.ok) throw await requestError(response, "conversation load");
    const payload = await response.json() as {conversation?: ConversationOrchestrationView};
    const view = payload.conversation ?? null;
    if (epoch === this.epoch && this.selectedThreadID === threadID) this.publish(view);
    return view;
  }

  clearSelection() {
    this.epoch++;
    this.selectedThreadID = null;
    // 用户显式放弃失败草稿后必须生成新的首条幂等键；原气泡内重试在未调用 clearSelection 时仍复用旧键。
    this.pending = null;
    this.createPendingKey = "";
    this.publish(null);
  }

  /** 无显式 Thread 时仅为兼容旧调用读取 current；新 UI 在成功回执绑定 Thread 后走 load。 */
  async refresh(): Promise<ConversationOrchestrationView | null> {
    if (this.selectedThreadID) return this.load(this.selectedThreadID);
    const epoch = ++this.epoch;
    const response = await this.request(`${API_ROOT}/current`, {cache: "no-store"});
    if (!response.ok) throw await requestError(response, "conversation refresh");
    const payload = await response.json() as {conversation: ConversationOrchestrationView | null};
    if (epoch === this.epoch) this.publish(payload.conversation);
    return payload.conversation;
  }

  /** 首条消息回执中的 conversation 是唯一绑定依据；不要依赖 current 猜测新会话。 */
  async sendFirst(text: string): Promise<ConversationOrchestrationView | null> {
    const result = await this.mutateRaw("messages", {text}, false);
    const thread = result?.conversation;
    if (!thread || !validThreadID(thread.id)) throw new Error("conversation first message did not return a thread");
    this.selectedThreadID = thread.id;
    return this.refreshAccepted();
  }
  send(text: string): Promise<ConversationOrchestrationView | null> {
    const view = this.requireView();
    const body = {text, expected_conversation_version: view.conversation.version, expected_focused_activity_id: view.conversation.focused_activity_id ?? ""};
    return this.mutate(`${view.conversation.id}/messages`, body);
  }
  confirm(): Promise<ConversationOrchestrationView | null> { return this.interact("confirm"); }
  cancel(): Promise<ConversationOrchestrationView | null> { return this.interact("cancel"); }
  acceptSwitch(resolution?: "wait" | "stop_and_switch"): Promise<ConversationOrchestrationView | null> { return this.resolveSwitch("accept", resolution); }
  rejectSwitch(): Promise<ConversationOrchestrationView | null> { return this.resolveSwitch("reject"); }
  acceptCancelProposal(): Promise<ConversationOrchestrationView | null> { return this.resolveCancelProposal("accept"); }
  rejectCancelProposal(): Promise<ConversationOrchestrationView | null> { return this.resolveCancelProposal("reject"); }
  acceptWorkflowProposal(): Promise<ConversationOrchestrationView | null> { return this.resolveWorkflowProposal("accept"); }
  rejectWorkflowProposal(): Promise<ConversationOrchestrationView | null> { return this.resolveWorkflowProposal("reject"); }

  private resolveWorkflowProposal(operation: "accept" | "reject") {
    const view = this.requireView(); const proposal = view.pending_workflow_proposal;
    if (!proposal || proposal.state !== "pending" || Date.parse(proposal.expires_at) <= Date.now()) throw new Error("conversation workflow proposal is unavailable");
    return this.mutate(`${view.conversation.id}/workflow-proposals/${proposal.id}/${operation}`, {expected_conversation_version: view.conversation.version, expected_proposal_version: proposal.version});
  }
  private resolveCancelProposal(operation: "accept" | "reject") {
    const view = this.requireView(); const decision = view.cancel_decision; const activity = view.activities?.find((item) => item.id === decision?.activity_id);
    if (!decision || decision.state !== "pending" || !activity) throw new Error("conversation cancel decision is unavailable");
    return this.mutate(`${view.conversation.id}/cancel-decisions/${decision.id}/${operation}`, {expected_conversation_version: view.conversation.version, expected_activity_version: activity.version, expected_decision_version: decision.version});
  }
  private resolveSwitch(operation: "accept" | "reject", resolution?: "wait" | "stop_and_switch") {
    const view = this.requireView(); const decision = view.switch_decision; const activity = view.activities?.find((item) => item.id === view.conversation.focused_activity_id);
    if (!decision || !activity || decision.state === "stopping" || (operation === "reject" && resolution)) throw new Error("conversation switch decision is unavailable");
    const body: Record<string, unknown> = {expected_conversation_version: view.conversation.version, expected_activity_version: activity.version, expected_decision_version: decision.version}; if (resolution) body.resolution = resolution;
    return this.mutate(`${view.conversation.id}/switch-decisions/${decision.id}/${operation}`, body);
  }
  private interact(operation: "confirm" | "cancel") {
    const view = this.requireView(); const activity = view.activities?.find((item) => item.id === view.conversation.focused_activity_id); const execution = view.focused_execution;
    const available = operation === "cancel" ? execution?.state === "waiting_user_input" || execution?.state === "waiting_user_confirmation" : execution?.state === "waiting_user_confirmation";
    if (!activity || !execution || !available) throw new Error("conversation interaction is unavailable");
    return this.mutate(`${view.conversation.id}/activities/${activity.id}/${operation}`, {expected_conversation_version: view.conversation.version, expected_activity_version: activity.version, expected_run_version: execution.run_version, expected_interaction_version: execution.interaction_version});
  }
  private mutate(path: string, body: object): Promise<ConversationOrchestrationView | null> {
    const fingerprint = mutationFingerprint(path, body); const existing = this.inFlight.get(fingerprint); if (existing) return existing;
    if (!this.pending || this.pending.fingerprint !== fingerprint) this.pending = {fingerprint, key: requestKey()};
    const promise = this.performMutation(path, body, fingerprint, this.pending.key).finally(() => this.inFlight.delete(fingerprint)); this.inFlight.set(fingerprint, promise); return promise;
  }
  private async mutateRaw(path: string, body: object, refreshAfter = true): Promise<{conversation?: ConversationThread} | null> {
    const fingerprint = mutationFingerprint(path, body);
    const existing = this.inFlight.get(fingerprint) as Promise<ConversationOrchestrationView | null> | undefined;
    if (existing) {
      const view = await existing;
      return view ? {conversation: view.conversation} : null;
    }
    if (!this.pending || this.pending.fingerprint !== fingerprint) this.pending = {fingerprint, key: requestKey()};
    const key = this.pending.key;
    const operation = (async () => {
      const response = await this.request(`${API_ROOT}/${path}`, {method: "POST", headers: {"content-type": "application/json", "idempotency-key": key}, body: JSON.stringify(body)});
      if (response.status === 409) { const conflict = await requestError(response, "conversation mutation"); this.pending = null; await this.refresh(); throw new ConversationRequestError("conversation changed; reloaded current state", conflict.status, conflict.code); }
      if (!response.ok) throw await requestError(response, "conversation mutation");
      if (this.pending?.fingerprint === fingerprint && this.pending.key === key) this.pending = null;
      this.channel?.postMessage({kind: "conversation-changed"});
      const payload = await response.json().catch(() => null) as {result?: MessageReceipt} | null;
      this.publishReceipt(payload?.result);
      if (refreshAfter) return await this.refreshAccepted();
      const thread = payload?.result?.conversation;
      return thread ? ({conversation: thread} as ConversationOrchestrationView) : null;
    })().finally(() => this.inFlight.delete(fingerprint));
    this.inFlight.set(fingerprint, operation);
    const view = await operation;
    return view ? {conversation: view.conversation} : null;
  }
  private async performMutation(path: string, body: object, fingerprint: string, key: string): Promise<ConversationOrchestrationView | null> {
    await this.mutateRawWithKey(path, body, fingerprint, key); return this.refreshAccepted();
  }
  private async mutateRawWithKey(path: string, body: object, fingerprint: string, key: string) {
    const response = await this.request(`${API_ROOT}/${path}`, {method: "POST", headers: {"content-type": "application/json", "idempotency-key": key}, body: JSON.stringify(body)});
    if (response.status === 409) { const conflict = await requestError(response, "conversation mutation"); this.pending = null; await this.refresh(); throw new ConversationRequestError("conversation changed; reloaded current state", conflict.status, conflict.code); }
    if (!response.ok) throw await requestError(response, "conversation mutation");
    if (this.pending?.fingerprint === fingerprint && this.pending.key === key) this.pending = null;
    const payload = await response.json().catch(() => null) as {result?: MessageReceipt} | null;
    this.publishReceipt(payload?.result);
    this.channel?.postMessage({kind: "conversation-changed"});
  }
  /** 成功回执即为权威消息，详情重读只负责补全投影；避免 POST 成功、GET 失败导致重复发送。 */
  private publishReceipt(receipt?: MessageReceipt) {
    const thread = receipt?.conversation;
    if (!thread || !validThreadID(thread.id)) return;
    if (this.selectedThreadID && this.selectedThreadID !== thread.id) return;
    const previous = this.view?.conversation.id === thread.id ? this.view : null;
    const messages = [...(previous?.messages ?? [])];
    for (const message of [receipt?.message, receipt?.assistant_message]) {
      if (message?.id && !messages.some(item => item.id === message.id)) messages.push(message);
    }
    const activities = [...(previous?.activities ?? [])];
    if (receipt?.activity) {
      const index = activities.findIndex(item => item.id === receipt.activity?.id);
      if (index < 0) activities.push(receipt.activity); else activities[index] = receipt.activity;
    }
    this.selectedThreadID = thread.id;
    this.publish({...previous, conversation: thread, messages, activities,
      focused_execution: receipt?.execution,
      pending_workflow_proposal: receipt?.pending_workflow_proposal});
  }
  private async refreshAccepted() {
    try { return await this.refresh(); }
    catch (error) {
      if (isConversationUnauthenticatedError(error)) throw error;
      throw new ConversationSyncError("message accepted; conversation sync pending");
    }
  }
  private requireView(): ConversationOrchestrationView { if (!this.view) throw new Error("conversation is unavailable"); return this.view; }
  private publish(view: ConversationOrchestrationView | null) { this.view = view; for (const listener of this.listeners) listener(view); }
}
